// src/components/CleanupModal.js

import React, { useState, useEffect } from 'react';

/**
 * CleanupModal
 *
 * The clean-up: select several lists, preview the merged and
 * deduplicated result, check what to keep, and apply. The result is one
 * new list; the sources are deleted.
 */
export default function CleanupModal({ kinId, lists, onClose, onApplied }) {
  const [step, setStep] = useState('select');   // 'select' | 'preview'
  const [selectedLists, setSelectedLists] = useState({});
  const [preview, setPreview] = useState(null);
  const [keep, setKeep] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newListName, setNewListName] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const toggleList = (id) => setSelectedLists((p) => ({ ...p, [id]: !p[id] }));
  const toggleKeep = (id) => setKeep((p) => ({ ...p, [id]: !p[id] }));

  const selectedIds = Object.keys(selectedLists).filter((k) => selectedLists[k]);

  const fetchPreview = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/lists/my/${kinId}/cleanup-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ listIds: selectedIds }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Preview failed'); }
      const data = await res.json();
      setPreview(data);
      const k = {};
      for (const it of data.proposed) k[it._id] = it.selected;
      setKeep(k);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setLoading(true);
    setError(null);
    try {
      const keepArr = Object.keys(keep).map((id) => ({ _id: id, selected: !!keep[id] }));
      const res = await fetch(`/api/lists/my/${kinId}/cleanup-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ listIds: selectedIds, keep: keepArr, name: newListName.trim() || undefined }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Apply failed'); }
      const data = await res.json();
      if (onApplied) onApplied(data.list);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const badge = (it) => {
    if (it.state === 'special') return { text: `On special ${it.savingPct}%`, cls: 'bg-green-100 text-green-800' };
    if (it.state === 'lower') return { text: `Down $${Math.abs(it.priceChange).toFixed(2)}`, cls: 'bg-green-100 text-green-800' };
    if (it.state === 'higher') return { text: `Up $${it.priceChange.toFixed(2)}`, cls: 'bg-red-100 text-red-800' };
    if (it.state === 'segment_off') return { text: 'Segment off', cls: 'bg-gray-100 text-gray-500' };
    if (it.state === 'unavailable') return { text: 'Unavailable', cls: 'bg-gray-100 text-gray-500' };
    return null;
  };

  const keepCount = Object.values(keep).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold">
            {step === 'select' ? 'Clean up lists' : 'Review the cleaned list'}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-lg">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}

          {step === 'select' && (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Select the lists to merge. The clean-up collapses duplicates, flags the dead products and the
                price changes, and gives you one list.
              </p>
              <div className="space-y-2">
                {lists.map((l) => (
                  <label key={l._id} className="flex items-center gap-2 border rounded-lg p-2 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={!!selectedLists[l._id]}
                      onChange={() => toggleList(l._id)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{l.name}</div>
                      <div className="text-xs text-gray-500">{l.itemCount} items</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          {step === 'preview' && preview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-center">
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-lg font-bold">{preview.summary.totalItems}</div>
                  <div className="text-[10px] text-gray-500">items</div>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-lg font-bold">{preview.summary.uniqueItems}</div>
                  <div className="text-[10px] text-gray-500">after dedupe</div>
                </div>
                <div className="bg-amber-50 rounded p-2">
                  <div className="text-lg font-bold">{preview.summary.duplicateCount}</div>
                  <div className="text-[10px] text-gray-500">duplicates</div>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-lg font-bold">{preview.summary.deadCount}</div>
                  <div className="text-[10px] text-gray-500">dead</div>
                </div>
              </div>

              <div className="space-y-2">
                {preview.proposed.map((it) => {
                  const b = badge(it);
                  return (
                    <label key={it._id} className="flex items-start gap-2 border rounded-lg p-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!keep[it._id]}
                        onChange={() => toggleKeep(it._id)}
                        className="mt-1 w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{it.name}</div>
                        <div className="text-xs text-gray-500">
                          {it.supplierId.replace('vhub:', '')} · ${(it.currentPrice ?? it.lastKnownPrice)?.toFixed(2)}
                          {it.duplicateCount > 0 && (
                            <span className="text-amber-700 ml-1">· {it.duplicateCount} duplicate{it.duplicateCount !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                        {b && <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${b.cls}`}>{b.text}</span>}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-4">
                <label className="block text-xs text-gray-600 font-medium mb-1">New list name</label>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder={`Cleaned ${new Date().toISOString().slice(0, 10)}`}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </>
          )}
        </div>

        <div className="p-3 border-t flex items-center justify-between gap-2">
          {step === 'select' ? (
            <>
              <span className="text-xs text-gray-500">{selectedIds.length} list{selectedIds.length !== 1 ? 's' : ''} selected</span>
              <button
                onClick={fetchPreview}
                disabled={selectedIds.length === 0 || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-300"
              >
                {loading ? 'Loading...' : 'Preview'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep('select')} className="text-sm text-gray-600 hover:text-gray-900">← Back</button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{keepCount} kept</span>
                <button
                  onClick={apply}
                  disabled={loading || keepCount === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-300"
                >
                  {loading ? 'Applying...' : `Create list (${keepCount})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
